import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import api from '../api';

const QRModal = ({ table, storeId, onClose }) => {
  const [qrUrl, setQrUrl] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const overlayRef = useRef(null);

  const tableNum = table?.tableNumber || table?.table_number || table?.tableNum;
  const tableId = table?._id || table?.id;

  useEffect(() => {
    const generateQR = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // 1. qr_url 가져오기 (테이블에 이미 있으면 바로 사용)
        let url = table?.qr_url || table?.qrUrl;

        if (!url) {
          const res = await api.get(`/stores/${storeId}/tables/${tableId}/qr`);
          url = res.data?.qr_url || res.data?.qrUrl;
        }

        if (!url) throw new Error('QR URL을 찾을 수 없습니다');

        setQrUrl(url);

        // 2. 브라우저에서 직접 QR 이미지 생성
        const dataUrl = await QRCode.toDataURL(url, {
          width: 280,
          margin: 2,
          color: { dark: '#191F28', light: '#FFFFFF' },
          errorCorrectionLevel: 'M',
        });
        setQrDataUrl(dataUrl);
      } catch (err) {
        setError('QR 코드 생성에 실패했습니다: ' + (err.message || ''));
      } finally {
        setIsLoading(false);
      }
    };

    if (tableId && storeId) generateQR();
    else { setIsLoading(false); setError('테이블 정보를 찾을 수 없습니다.'); }
  }, [tableId, storeId]);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleDownload = () => {
    if (!qrDataUrl) return;
    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = `table-${tableNum}-qr.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>테이블 ${tableNum}번 QR 코드</title>
          <style>
            body { font-family: sans-serif; text-align: center; padding: 40px; }
            img { width: 260px; height: 260px; }
            h2 { margin-bottom: 16px; color: #191F28; }
            p { font-size: 13px; color: #8B95A1; margin-top: 12px; word-break: break-all; }
            .brand { font-size: 20px; font-weight: 700; color: #BF9B30; margin-bottom: 8px; }
          </style>
        </head>
        <body>
          <div class="brand">스마트오더</div>
          <h2>테이블 ${tableNum}번</h2>
          <img src="${qrDataUrl}" alt="QR Code" />
          <p>QR 코드를 스캔하여 주문하세요</p>
          <p style="font-size:11px;color:#B2BEC3;">${qrUrl || ''}</p>
          <script>window.onload = function() { window.print(); }<` + `/script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div
      className="modal-overlay"
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      <div className="modal" style={{ maxWidth: 400 }}>
        <div className="modal-header">
          <h3 className="modal-title">테이블 {tableNum}번 QR 코드</h3>
          <button className="modal-close" onClick={onClose}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
        </div>

        <div className="modal-body">
          <div style={{ textAlign: 'center' }}>
            {isLoading ? (
              <div style={{ padding: '60px 0', display: 'flex', justifyContent: 'center' }}>
                <div className="spinner spinner-dark" style={{ width: 36, height: 36 }} />
              </div>
            ) : error ? (
              <div style={{ padding: '40px 0', color: 'var(--danger)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                {error}
              </div>
            ) : (
              <>
                {/* QR 이미지 */}
                <div style={{
                  background: '#fff',
                  borderRadius: 16,
                  padding: 16,
                  display: 'inline-block',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  marginBottom: 16,
                }}>
                  <img src={qrDataUrl} alt={`테이블 ${tableNum} QR`} style={{ display: 'block', width: 240, height: 240 }} />
                </div>

                {/* URL */}
                <div style={{
                  fontSize: '0.72rem',
                  color: 'var(--text-tertiary)',
                  background: 'var(--bg)',
                  borderRadius: 8,
                  padding: '8px 12px',
                  marginBottom: 16,
                  wordBreak: 'break-all',
                  textAlign: 'left',
                }}>
                  {qrUrl}
                </div>

                <p style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', marginBottom: 20 }}>
                  이 QR 코드를 테이블에 부착하면 고객이 스캔하여 바로 주문할 수 있습니다.
                </p>

                {/* 버튼 */}
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="btn btn-primary" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }} onClick={handleDownload}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    다운로드
                  </button>
                  <button className="btn btn-outline" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }} onClick={handlePrint}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="6 9 6 2 18 2 18 9"/>
                      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                      <rect x="6" y="14" width="12" height="8"/>
                    </svg>
                    인쇄
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRModal;
